import React, { useEffect, useState } from 'react';
import { AcademyMediaAsset } from '../../services/trainingService';

interface LessonMediaPickerProps {
  assets: AcademyMediaAsset[];
  attachedIds: number[];
  onAttach: (asset: AcademyMediaAsset) => void;
  disabled?: boolean;
}

const assetKind = (asset: AcademyMediaAsset): string => {
  if (asset.content_type.startsWith('image/')) return 'image';
  if (asset.content_type.startsWith('video/')) return 'video';
  return 'file';
};

const LessonMediaPicker: React.FC<LessonMediaPickerProps> = ({
  assets,
  attachedIds,
  onAttach,
  disabled = false,
}) => {
  const [selectedId, setSelectedId] = useState('');
  const available = assets.filter((asset) => !attachedIds.includes(asset.id));

  useEffect(() => {
    if (
      selectedId &&
      !available.some((asset) => String(asset.id) === selectedId)
    ) {
      setSelectedId('');
    }
  }, [available, selectedId]);

  if (!assets.length) {
    return (
      <p className="mt-3 text-[11px] text-slate-500">
        No files in the media library yet. Upload one above to reuse it in other lessons.
      </p>
    );
  }

  if (!available.length) {
    return (
      <p className="mt-3 text-[11px] text-slate-500">
        Every media-library file is already attached to this lesson.
      </p>
    );
  }

  const selected = available.find((asset) => String(asset.id) === selectedId);

  return (
    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
      <label className="min-w-0 flex-1 text-[11px] font-semibold text-slate-600">
        Attach from library
        <select
          value={selectedId}
          disabled={disabled}
          onChange={(event) => setSelectedId(event.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-normal text-slate-900 focus:border-slate-500 focus:outline-none disabled:opacity-50"
        >
          <option value="">Choose a media file…</option>
          {available.map((asset) => (
            <option key={asset.id} value={asset.id}>
              {asset.filename} ({assetKind(asset)})
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        disabled={!selected || disabled}
        onClick={() => {
          if (!selected) return;
          onAttach(selected);
          setSelectedId('');
        }}
        className="self-end rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 sm:self-auto"
      >
        Attach
      </button>
    </div>
  );
};

export default LessonMediaPicker;
